<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import { Ban, Check, CheckCircle, ChevronLeft, Loader2, RefreshCcw, XCircle } from 'lucide-vue-next'
import { apiGetMembershipPlans, apiQueryPaymentStatus } from '@/apis/membership'
import type { IMembershipPlan, IReqQueryPaymentStatus } from '@/types/apis/membership'
import { PaymentProvider, PaymentStatus } from '@/enums/membership'
import { useAppStore } from '@/stores/app'
import { useUserStore } from '@/stores/user'
import { getLocale } from '@/i18n'

const route = useRoute()
const router = useRouter()
const { t } = useI18n()
const appStore = useAppStore()
const userStore = useUserStore()

const pollingIntervalMs = 2000
const pollingTimeoutMs = 5 * 60 * 1000

const getQueryParam = (key: string) => {
  const raw = route.query[key]
  if (Array.isArray(raw)) return raw[0] ?? ''
  return typeof raw === 'string' ? raw : ''
}

const parsePaymentProvider = (raw: string) => {
  const value = Number(raw)
  if (value === PaymentProvider.PayPal || value === PaymentProvider.Alipay || value === PaymentProvider.WeChat) {
    return value as PaymentProvider
  }
  return null
}

const parsePlanId = (raw: string) => {
  if (!raw) return null
  const value = Number(raw)
  return Number.isFinite(value) ? value : null
}

const resolveChannelOrderId = (provider: PaymentProvider) => {
  switch (provider) {
    case PaymentProvider.PayPal:
      return getQueryParam('channelOrderId')
    case PaymentProvider.Alipay:
      return getQueryParam('trade_no') || getQueryParam('channelOrderId')
    default:
      return ''
  }
}

const parseOrderParams = (): IReqQueryPaymentStatus | null => {
  const orderNo = getQueryParam('orderNo')
  const paymentProvider = parsePaymentProvider(getQueryParam('paymentProvider'))
  if (!orderNo || paymentProvider === null) return null

  const channelOrderId = resolveChannelOrderId(paymentProvider)

  return {
    OrderNo: orderNo,
    PaymentProvider: paymentProvider,
    ChannelOrderId: channelOrderId,
  }
}

const initialLoading = ref(true)
const queryLoading = ref(false)
const invalidParams = ref(false)
const timeoutReached = ref(false)
const isFinalStatus = ref(false)
const currentStatus = ref<PaymentStatus>(PaymentStatus.Processing)
const orderParams = ref<IReqQueryPaymentStatus | null>(null)
const pollingStartAt = ref(0)
const pollTimer = ref<number | null>(null)
const plan = ref<IMembershipPlan | null>(null)
const queryPlanId = ref<number | null>(null)
const resolvedPlanId = ref<number | null>(null)

const clearPollTimer = () => {
  if (pollTimer.value !== null) {
    window.clearInterval(pollTimer.value)
    pollTimer.value = null
  }
}

const startPolling = () => {
  clearPollTimer()
  pollTimer.value = window.setInterval(() => void queryPaymentStatus(), pollingIntervalMs)
}

const isWaiting = computed(() => !invalidParams.value && !isFinalStatus.value)
const isPointsOrder = computed(() => queryPlanId.value === 0 || resolvedPlanId.value === 0)
const purchaseEntryPath = computed(() => (isPointsOrder.value ? '/plan/points' : '/plan'))
const purchaseEntryLabel = computed(() =>
  isPointsOrder.value ? t('plan.page.result.backToPoints') : t('plan.page.result.backToPlan'),
)
const repurchaseLabel = computed(() =>
  isPointsOrder.value ? t('plan.page.result.buyPointsAgain') : t('plan.page.result.buyAgain'),
)

const formattedPrice = computed(() => {
  if (!plan.value) return '--'
  return `￥${plan.value.PriceCNY.toFixed(2)}`
})

const statusConfig = computed(() => {
  const isPoints = isPointsOrder.value

  switch (currentStatus.value) {
    case PaymentStatus.Paid:
      return {
        icon: CheckCircle,
        iconClass: 'text-green-400',
        bgClass: 'bg-green-400/10 border-green-400/25',
        title: t(isPoints ? 'plan.page.result.pointsPaidTitle' : 'plan.page.result.paidTitle'),
        desc: t(isPoints ? 'plan.page.result.pointsPaidDesc' : 'plan.page.result.paidDesc'),
        isPaid: true,
      }
    case PaymentStatus.Refunded:
      return {
        icon: RefreshCcw,
        iconClass: 'text-sky-400',
        bgClass: 'bg-sky-400/10 border-sky-400/25',
        title: t(isPoints ? 'plan.page.result.pointsRefundedTitle' : 'plan.page.result.refundedTitle'),
        desc: t(isPoints ? 'plan.page.result.pointsRefundedDesc' : 'plan.page.result.refundedDesc'),
        isPaid: false,
      }
    case PaymentStatus.Failed:
      return {
        icon: XCircle,
        iconClass: 'text-red-400',
        bgClass: 'bg-red-400/10 border-red-400/25',
        title: t(isPoints ? 'plan.page.result.pointsFailedTitle' : 'plan.page.result.failedTitle'),
        desc: t(isPoints ? 'plan.page.result.pointsFailedDesc' : 'plan.page.result.failedDesc'),
        isPaid: false,
      }
    case PaymentStatus.Cancelled:
      return {
        icon: Ban,
        iconClass: 'text-white/40',
        bgClass: 'bg-white/5 border-white/10',
        title: t(isPoints ? 'plan.page.result.pointsCancelledTitle' : 'plan.page.result.cancelledTitle'),
        desc: t(isPoints ? 'plan.page.result.pointsCancelledDesc' : 'plan.page.result.cancelledDesc'),
        isPaid: false,
      }
    default:
      return {
        icon: XCircle,
        iconClass: 'text-white/40',
        bgClass: 'bg-white/5 border-white/10',
        title: t(isPoints ? 'plan.page.result.pointsUnknownTitle' : 'plan.page.result.unknownTitle'),
        desc: t(isPoints ? 'plan.page.result.pointsUnknownDesc' : 'plan.page.result.unknownDesc'),
        isPaid: false,
      }
  }
})

const features = computed(() =>
  getLocale() === 'zh-CN' ? (plan.value?.Features ?? []) : (plan.value?.FeaturesEn ?? []),
)

const pointsDetailDescription = computed(() => {
  if (statusConfig.value.isPaid) {
    return t('plan.page.result.pointsDetailPaidDesc', { balance: userStore.profileInfo?.AvailablePoints ?? 0 })
  }

  return t('plan.page.result.pointsDetailDesc')
})

const pointsBalanceText = computed(() =>
  t('plan.page.result.pointsBalance', { num: userStore.profileInfo?.AvailablePoints ?? 0 }),
)

const loadPlan = async (planId: number) => {
  try {
    const res = await apiGetMembershipPlans()
    plan.value = res.Body.Plans?.find(p => p.Id === planId) ?? null
  } catch (error) {
    console.error('load plan failed', error)
    plan.value = null
  }
}

const queryPaymentStatus = async (force = false) => {
  if (!orderParams.value || queryLoading.value || isFinalStatus.value || invalidParams.value) return

  if (!force && Date.now() - pollingStartAt.value >= pollingTimeoutMs) {
    clearPollTimer()
    timeoutReached.value = true
    return
  }

  queryLoading.value = true
  try {
    const res = await apiQueryPaymentStatus(orderParams.value)
    const status = res.Body.PaymentStatus as PaymentStatus
    const responsePlanId = typeof res.Body.PlanId === 'number' ? res.Body.PlanId : null
    const shouldUsePointsBranch = queryPlanId.value === 0 || responsePlanId === 0
    currentStatus.value = status

    if (res.Body.IsFinalStatus) {
      isFinalStatus.value = true
      timeoutReached.value = false
      clearPollTimer()
      resolvedPlanId.value = responsePlanId ?? resolvedPlanId.value

      if (status === PaymentStatus.Paid) {
        await userStore.refreshProfileInfo()
      }

      if (!shouldUsePointsBranch && responsePlanId !== null && responsePlanId > 0) {
        await loadPlan(responsePlanId)
      } else {
        plan.value = null
      }
    }
  } catch (error) {
    console.error('query payment status failed', error)
  } finally {
    queryLoading.value = false
    initialLoading.value = false
  }
}

const handleRetry = async () => {
  if (invalidParams.value) {
    router.push(purchaseEntryPath.value)
    return
  }

  timeoutReached.value = false
  pollingStartAt.value = Date.now()
  await queryPaymentStatus(true)
  if (!isFinalStatus.value) {
    startPolling()
  }
}

const handleVisibilityChange = () => {
  if (document.visibilityState === 'visible') {
    void queryPaymentStatus(true)
  }
}

onMounted(async () => {
  queryPlanId.value = parsePlanId(getQueryParam('planId'))
  resolvedPlanId.value = queryPlanId.value
  orderParams.value = parseOrderParams()
  if (!orderParams.value) {
    invalidParams.value = true
    initialLoading.value = false
    return
  }

  pollingStartAt.value = Date.now()
  document.addEventListener('visibilitychange', handleVisibilityChange)

  await queryPaymentStatus(true)
  if (!isFinalStatus.value && !timeoutReached.value) {
    startPolling()
  }
})

onBeforeUnmount(() => {
  clearPollTimer()
  document.removeEventListener('visibilitychange', handleVisibilityChange)
})
</script>

<template>
  <div class="min-h-screen overflow-y-auto bg-gray-950 text-white">
    <div class="mx-auto max-w-lg px-4 pb-16 pt-5 sm:px-6">
      <button class="mb-8 inline-flex items-center gap-2 text-sm text-white/80" @click="router.push('/profile')">
        <ChevronLeft :size="24" />
        {{ t('plan.page.back') }}
      </button>

      <div v-if="initialLoading" class="flex flex-col items-center justify-center gap-4 py-24">
        <Loader2 :size="36" class="animate-spin text-white/40" />
        <p class="text-sm text-white/45">{{ t('plan.page.result.checkingOrder') }}</p>
      </div>

      <div
        v-else-if="invalidParams"
        class="overflow-hidden rounded-[28px] border border-white/10 bg-white/5 px-6 py-10 text-center shadow-[0_24px_80px_rgba(15,23,42,0.5)]"
      >
        <XCircle :size="appStore.isMobile ? 56 : 64" class="mx-auto text-red-400" />
        <h1 class="mt-4 text-2xl font-semibold">{{ t('plan.page.result.invalidParamsTitle') }}</h1>
        <p class="mx-auto mt-2 max-w-xs text-sm leading-6 text-white/60">
          {{ t('plan.page.result.invalidParamsDesc') }}
        </p>
        <button
          class="mt-6 flex h-12 w-full items-center justify-center rounded-2xl bg-amber-400 text-base font-semibold text-slate-900 transition hover:bg-amber-300"
          @click="router.push(purchaseEntryPath)"
        >
          {{ purchaseEntryLabel }}
        </button>
      </div>

      <div
        v-else-if="isWaiting"
        class="overflow-hidden rounded-[28px] border border-white/10 bg-white/5 px-6 py-10 text-center shadow-[0_24px_80px_rgba(15,23,42,0.5)]"
      >
        <Loader2
          v-if="!timeoutReached"
          :size="appStore.isMobile ? 56 : 64"
          class="mx-auto animate-spin text-amber-300"
        />
        <RefreshCcw v-else :size="appStore.isMobile ? 56 : 64" class="mx-auto text-sky-300" />
        <h1 class="mt-4 text-2xl font-semibold">
          {{ timeoutReached ? t('plan.page.result.timeoutTitle') : t('plan.page.result.pendingTitle') }}
        </h1>
        <p class="mx-auto mt-2 max-w-xs text-sm leading-6 text-white/60">
          {{ timeoutReached ? t('plan.page.result.timeoutDesc') : t('plan.page.result.pendingDesc') }}
        </p>
        <div class="mt-6 flex flex-col gap-3">
          <button
            class="flex h-12 w-full items-center justify-center rounded-2xl bg-amber-400 text-base font-semibold text-slate-900 transition hover:bg-amber-300 disabled:cursor-not-allowed disabled:opacity-60"
            :disabled="queryLoading"
            @click="handleRetry"
          >
            <Loader2 v-if="queryLoading" :size="16" class="animate-spin" />
            <span v-else>{{ t('plan.page.result.retryQuery') }}</span>
          </button>
          <button
            class="flex h-12 w-full items-center justify-center rounded-2xl border border-white/15 bg-white/5 text-base text-white/80 transition hover:bg-white/10"
            @click="router.push(purchaseEntryPath)"
          >
            {{ purchaseEntryLabel }}
          </button>
        </div>
      </div>

      <template v-else>
        <div
          class="flex flex-col items-center gap-4 overflow-hidden rounded-[28px] border px-6 py-10 text-center shadow-[0_24px_80px_rgba(15,23,42,0.5)]"
          :class="statusConfig.bgClass"
        >
          <component :is="statusConfig.icon" :size="appStore.isMobile ? 56 : 64" :class="statusConfig.iconClass" />
          <div>
            <h1 class="text-2xl font-semibold">{{ statusConfig.title }}</h1>
            <p class="mt-2 max-w-xs text-sm leading-6 text-white/60">{{ statusConfig.desc }}</p>
          </div>
        </div>

        <div
          v-if="plan"
          class="mt-5 overflow-hidden rounded-[28px] border border-white/10 bg-[linear-gradient(180deg,rgba(255,255,255,0.06),rgba(255,255,255,0.02))] px-6 py-6 shadow-[0_8px_40px_rgba(15,23,42,0.3)]"
        >
          <div class="flex items-start justify-between gap-3">
            <div>
              <div class="text-lg font-semibold">{{ plan.PlanName }}</div>
              <div class="mt-1 text-xs text-white/50">
                {{ t('plan.page.result.validityDays', { days: plan.DurationDays }) }}
              </div>
            </div>
            <div class="shrink-0 rounded-full bg-white/8 px-3 py-1 text-xs text-white/70">
              {{ t('plan.page.result.giftPoints', { num: plan.GiftPoints }) }}
            </div>
          </div>

          <div class="mt-5 flex items-end gap-2">
            <span class="text-3xl font-semibold text-white">{{ formattedPrice }}</span>
            <span v-if="plan.OriginalPriceCNY > plan.PriceCNY" class="pb-1 text-sm text-white/35 line-through">
              ￥{{ plan.OriginalPriceCNY.toFixed(2) }}
            </span>
          </div>

          <p class="mt-4 text-sm leading-6 text-white/60">
            {{ plan.Description }}
          </p>

          <div v-if="statusConfig.isPaid && features.length > 0" class="mt-5 flex flex-col gap-2">
            <div v-for="feature in features" :key="feature" class="flex items-center gap-2 text-sm text-white/75">
              <Check :size="15" class="shrink-0 text-amber-400" />
              {{ feature }}
            </div>
          </div>
        </div>

        <div
          v-else-if="isPointsOrder"
          class="mt-5 overflow-hidden rounded-[28px] border border-white/10 bg-[linear-gradient(180deg,rgba(255,255,255,0.06),rgba(255,255,255,0.02))] px-6 py-6 shadow-[0_8px_40px_rgba(15,23,42,0.3)]"
        >
          <div class="flex items-start justify-between gap-3">
            <div>
              <div class="text-lg font-semibold">{{ t('plan.page.result.pointsDetailTitle') }}</div>
              <div class="mt-1 text-xs text-white/50">
                {{ statusConfig.isPaid ? pointsBalanceText : t('plan.page.result.pointsOrderTag') }}
              </div>
            </div>
            <div class="shrink-0 rounded-full bg-amber-300/10 px-3 py-1 text-xs text-amber-100/80">
              {{ t('plan.page.result.pointsOrderTag') }}
            </div>
          </div>

          <p class="mt-4 text-sm leading-6 text-white/60">
            {{ pointsDetailDescription }}
          </p>
        </div>

        <div
          v-else
          class="mt-5 rounded-[28px] border border-white/10 bg-white/3 px-6 py-6 text-center text-sm text-white/45"
        >
          {{ t('plan.page.result.unknownPlan') }}
        </div>

        <div class="mt-8 flex flex-col gap-3">
          <button
            v-if="statusConfig.isPaid"
            class="flex h-12 w-full items-center justify-center rounded-2xl bg-amber-400 text-base font-semibold text-slate-900 transition hover:bg-amber-300"
            @click="router.push('/profile')"
          >
            {{ t('plan.page.result.goProfile') }}
          </button>
          <template v-else>
            <button
              class="flex h-12 w-full items-center justify-center rounded-2xl bg-amber-400 text-base font-semibold text-slate-900 transition hover:bg-amber-300"
              @click="router.push(purchaseEntryPath)"
            >
              {{ repurchaseLabel }}
            </button>
            <button
              class="flex h-12 w-full items-center justify-center rounded-2xl border border-white/15 bg-white/5 text-base text-white/80 transition hover:bg-white/10"
              @click="router.push('/profile')"
            >
              {{ t('plan.page.result.goProfile') }}
            </button>
          </template>
        </div>
      </template>
    </div>
  </div>
</template>
